import { RUN_HEARTBEAT_EVENT } from '@developerz/domain';
import { createMemo, createSignal, For, type JSX } from 'solid-js';
import { parseStatusEventName, type RunEventRow } from '../lib/fleetStream';
import { RunEventNode } from './RunEventNode';
import { firstRunFailure, runEventNodeId } from './RunOutcomeBanner';
import { RunTimelineFilters } from './RunTimelineFilters';

// Vertical `.timeline` rail for one fleet run (spec §3.13). Mirrors
// <AuditTimeline/> but reads the run-event shape. One node per event
// (<RunEventNode/>), minus the noise this component filters out.
//
// THE HEARTBEAT IS NOT PROGRESS, and folding it is not cosmetic. It is emitted
// on a fixed cadence precisely BECAUSE nothing is happening — its payload is
// `silent_ms`, the time since the run last did anything real — and prod carries
// 10,979 of them, the single largest sub-shape in `task_run_events`. The reaper
// in `packages/db` excludes it from its progress proof for the same reason
// (`RUN_HEARTBEAT_EVENT` is in the domain so both seams agree about the token,
// which is why this imports it rather than spelling it again).
//
// Hidden BY DEFAULT, and the count is stated: a filter that silently drops rows
// is a timeline missing lines. One click brings them back.

export type FleetRunTimelineProps = {
  readonly events: readonly RunEventRow[];
};

/** True for the liveness beat — the one row whose whole content is "nothing happened". */
export function isHeartbeat(ev: RunEventRow): boolean {
  return parseStatusEventName(ev) === RUN_HEARTBEAT_EVENT;
}

export function FleetRunTimeline(props: FleetRunTimelineProps): JSX.Element {
  const [noiseHidden, setNoiseHidden] = createSignal(true);
  const heartbeats = createMemo(() => props.events.filter(isHeartbeat).length);
  const shown = createMemo(() =>
    noiseHidden() ? props.events.filter((ev) => !isHeartbeat(ev)) : props.events,
  );
  // The anchor is the FAILURE's own node, so the jump lands on the row that
  // carries the message — never on a filtered-out neighbour.
  const errorHref = createMemo(() => {
    const failure = firstRunFailure(props.events);
    return failure === null ? null : `#${runEventNodeId(failure.seq)}`;
  });

  return (
    <>
      <RunTimelineFilters
        hiddenCount={heartbeats()}
        noiseHidden={noiseHidden()}
        onToggleNoise={() => setNoiseHidden((on) => !on)}
        errorHref={errorHref()}
      />
      <ol class="timeline fleet-timeline">
        <For each={shown()}>{(ev) => <RunEventNode event={ev} />}</For>
      </ol>
    </>
  );
}
